import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaEye, FaJediOrder } from 'react-icons/fa';
import { FaCartShopping } from 'react-icons/fa6';
import { makeOrderAsync } from '../../../features/order/orderSlice';
import { toast } from 'react-toastify';

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const { product_id, name, weight, purity, cover_image, making_charge, quantity, registered_firm_name } = product;
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showCartDialog, setShowCartDialog] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const maxQuantity = quantity || 0;

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 0 && value <= maxQuantity) {
      setSelectedQuantity(value);
    }
  };

  const handlePlaceOrder = async () => {
    if (selectedQuantity > 0) {
      try {
        const orderData = {
          product_id,
          quantity: selectedQuantity,
        };
        
        await dispatch(makeOrderAsync({ 
          type: 'ORDER',
          orderData 
        })).unwrap();
        
        toast.success('Order placed successfully!');
        setShowOrderDialog(false);
        setSelectedQuantity(0);
      } catch (error) {
        toast.error(error.message || 'Failed to place order');
      }
    }
  };
  
  const handlePlaceCart = async () => {
    if (selectedQuantity > 0) {
      try {
        const orderData = {
          product_id,
          quantity: selectedQuantity,
        };
        
        await dispatch(makeOrderAsync({ 
          type: 'CART',
          orderData 
        })).unwrap();
        
        toast.success('Added to cart successfully!');
        setShowCartDialog(false);
        setSelectedQuantity(0);
      } catch (error) {
        toast.error(error.message || 'Failed to add to cart');
      }
    }
  };

  const handleOrder = () => {
    setShowOrderDialog(true);
    setSelectedQuantity(0);
  };

  const handleCart = () => {
    setShowCartDialog(true);
    setSelectedQuantity(0);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      {/* Product Image */}
      <div className="relative h-48 w-full">
        <img
          src={cover_image}
          alt={name}
          className="w-full h-full object-cover"
        />
        {registered_firm_name && (
          <div className="absolute bottom-2 right-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
            {registered_firm_name}
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">{name}</h3>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div>
            <p className="text-sm text-gray-500">Weight</p>
            <p className="font-medium">{weight} g</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Purity</p>
            <p className="font-medium">{purity}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Making Charge</p>
            <p className="font-medium">{(making_charge!=0)?making_charge+'%':' - '}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Quantity</p>
            <p className="font-medium">{quantity}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 space-y-2">
          <Link
            to={`/retailer/dashboard/${product_id}`}
            className="flex items-center justify-center w-full bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors duration-300"
          >
            <FaEye className="mr-2" /> More Details
          </Link>
          
          <div className="flex space-x-2">
            <button
              onClick={handleCart}
              className="flex items-center justify-center w-full bg-red-500 text-white text-center px-4 py-2 rounded-md hover:bg-red-600 transition-colors duration-300"
            >
              <FaCartShopping className="mr-2" /> Cart
            </button>
            <button
              onClick={handleOrder}
              className="flex items-center justify-center w-full bg-blue-500 text-white text-center px-4 py-2 rounded-md hover:bg-blue-600 transition-colors duration-300"
            >
              <FaJediOrder className="mr-2" /> Order
            </button>
          </div>
        </div>
      </div>

      {/* Order Dialog */}
      {showOrderDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Place Order</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Product Name</p>
                  <p className="text-sm text-gray-900">{name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Product ID</p>
                  <p className="text-sm text-gray-900">{product_id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Available Quantity</p>
                  <p className="text-sm text-gray-900">{maxQuantity}</p>
                </div>
              </div>
              
              <div>
                <label htmlFor={`quantity-${product_id}`} className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  id={`quantity-${product_id}`}
                  min="0"
                  max={maxQuantity}
                  value={selectedQuantity}
                  onChange={handleQuantityChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Maximum available: {maxQuantity}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setShowOrderDialog(false);
                    setSelectedQuantity(0);
                  }}
                  className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePlaceOrder}
                  disabled={selectedQuantity <= 0}
                  className={`w-full px-4 py-2 rounded-md transition-colors duration-300 ${
                    selectedQuantity > 0
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Place Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Dialog */}
      {showCartDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add to Cart</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Product Name</p>
                  <p className="text-sm text-gray-900">{name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Product ID</p>
                  <p className="text-sm text-gray-900">{product_id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Available Quantity</p>
                  <p className="text-sm text-gray-900">{maxQuantity}</p>
                </div>
              </div>
              
              <div>
                <label htmlFor={`cart-quantity-${product_id}`} className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  id={`cart-quantity-${product_id}`}
                  min="0"
                  max={maxQuantity}
                  value={selectedQuantity}
                  onChange={handleQuantityChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Maximum available: {maxQuantity}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setShowCartDialog(false);
                    setSelectedQuantity(0);
                  }}
                  className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePlaceCart}
                  disabled={selectedQuantity <= 0}
                  className={`w-full px-4 py-2 rounded-md transition-colors duration-300 ${
                    selectedQuantity > 0
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCard;